/** moderator/moderation — HELPERS.
 *
 * The game server's moderation answers read into the portal's shapes, once:
 * the moderator's match list and the admin's interruption check both read the
 * list through parseMatchList (by way of moderationApi.listMatches), where
 * each had read the raw answer its own way. */

import type {
  Game,
  GameWorld,
  MatchPlayer,
  PlayerDetails,
  ModerationCohortWire,
  ModerationListWire,
  ModerationPlayerDetailsWire,
  ModerationPlayerWire,
  ModerationWorldWire,
  PlayerCohort,
} from './types';

/**
 * A player's details could not be read: they are online and their match was
 * too busy to say (details_unreadable). Said as that, where any other failed
 * read is said as a failure.
 */
export class PlayerMatchBusyError extends Error {
  constructor() {
    super("The player's match is busy: their details could not be read");
    this.name = 'PlayerMatchBusyError';
  }
}

/**
 * A player's details, as their card reads them, with the match they are in
 * now — which the card's actions go to, where each searched the match list
 * for the player; fails when their match was too busy to read.
 */
export function parsePlayerDetails(answer: ModerationPlayerDetailsWire): PlayerDetails {
  if (answer.details_unreadable) throw new PlayerMatchBusyError();
  return { ...parsePlayer(answer), currentMatchId: answer.current_match_id || null };
}

/** A player, as the list and a player's details both name one. */
function parsePlayer(player: ModerationPlayerWire | ModerationPlayerDetailsWire): MatchPlayer {
  return {
    id: player.id,
    name: player.name,
    customId: player.custom_id,
    role: (player.role || 'student') as 'student' | 'moderator',
    helpRequestType: (player.help_request_type || '') as 'individual' | 'cohort' | '',
    helpRequestedAt: player.help_requested_at || 0,
    isInPrivateChat: player.is_in_private_chat || false,
  };
}

function parseCohort(cohort: ModerationCohortWire): PlayerCohort {
  return {
    id: cohort.id,
    name: cohort.name,
    lastTickProfit: cohort.last_tick_profit,
    lastQuarterProfit: cohort.last_quarter_profit,
    sessionScriptActivityId: cohort.session_script_activity_id,
    sessionScriptPhaseType: cohort.session_script_phase_type,
    sessionScriptPhaseIndex: cohort.session_script_phase_index,
    sessionScriptPhaseCount: cohort.session_script_phase_count,
    sessionScriptActivityDone: cohort.session_script_activity_done,
    players: (cohort.players ?? []).map(parsePlayer),
  };
}

function parseWorld(world: ModerationWorldWire): GameWorld {
  return {
    id: world.id,
    name: world.name,
    sessionStartTime: world.session_start_time,
    matchUUID: world.match_uuid,
    sessionTimestamp: world.session_timestamp,
    sessionScriptId: world.session_script_id,
    sessionScriptStepIds: world.session_script_step_ids,
    sessionScriptStepIndex: world.session_script_step_index,
    sessionScriptStepState: world.session_script_step_state,
    detailsUnreadable: world.details_unreadable === true,
    cohorts: (world.cohorts ?? []).map(parseCohort),
  };
}

/**
 * The game server's matches, as the moderation list answers them. An answer
 * that is not the list — no `games` array — is no list, and fails: read as no
 * matches it would say every running match was gone, the moderator's screen
 * "No matches found" and the admin's interruption check every running
 * session interrupted.
 */
export function parseMatchList(answer: unknown): readonly Game[] {
  const games = (answer as Partial<ModerationListWire> | null | undefined)?.games;
  if (!Array.isArray(games)) {
    throw new Error('The match list answered without its games');
  }
  return games.map((game) => ({
    id: game.id,
    name: game.name,
    worlds: (game.worlds ?? []).map(parseWorld),
  }));
}
